// src/components/CompanionCreator.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
  createCompanion,
  COMPANION_EXPERIENCES,
  COMPANION_UPGRADES
} from '../services/companionService';
import {
  uploadCompanionImage,
  validateImageFile
} from '../services/imageUploadService';

const IconChevronLeft = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="m15 18-6-6 6-6"/>
  </svg>
);

const IconCheck = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const CompanionCreator = ({ rangerId: rangerIdProp, onComplete }) => {
  const navigate = useNavigate();
  const { characterId: rangerIdParam } = useParams();
  const { user } = useAuth();
  const rangerId = rangerIdProp || rangerIdParam;

  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [ranger, setRanger] = useState(null);
  const [loading, setLoading] = useState(true);

  const [companionData, setCompanionData] = useState({
    name: '',
    animalType: '',
    imageUrl: '',
    experiences: [],
    attackDescription: ''
  });

  // Track upgrade selections for level 2+
  const [upgradeSelections, setUpgradeSelections] = useState([]);

  const [customExperience, setCustomExperience] = useState('');

  // Load ranger data to determine if upgrades are needed
  useEffect(() => {
    const loadRanger = async () => {
      if (!rangerId) return;

      try {
        const rangerRef = doc(db, 'characters', rangerId);
        const rangerSnap = await getDoc(rangerRef);

        if (rangerSnap.exists()) {
          setRanger({ id: rangerSnap.id, ...rangerSnap.data() });
        }
      } catch (error) {
        console.error('Error loading ranger:', error);
      } finally {
        setLoading(false);
      }
    };

    loadRanger();
  }, [rangerId]);

  // Calculate how many upgrades are needed
  const upgradesNeeded = ranger && ranger.level > 1 ? ranger.level - 1 : 0;

  // Dynamic total steps based on ranger level
  const totalSteps = 4 + upgradesNeeded;

  // Step navigation
  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1:
        return companionData.name.trim() && companionData.animalType.trim();
      case 2:
        return companionData.experiences.length === 2;
      case 3:
        return companionData.attackDescription.trim();
      case 4:
        return true; // Review step
      default:
        // Upgrade steps (5+)
        const upgradeStepIndex = step - 5;
        return upgradeSelections[upgradeStepIndex] !== undefined;
    }
  };

  // Handle image file selection
  const handleImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', file.name, file.type, file.size);

    const validation = validateImageFile(file);

    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    setImageFile(file);
    setImagePreview(validation.preview);
    toast.success(`Selected: ${file.name}`);
  };

  // Trigger file input click
  const triggerFileInput = () => {
    const fileInput = document.getElementById('companion-image-upload');
    if (fileInput) {
      fileInput.click();
    }
  };

  // Remove selected image
  const handleRemoveImage = () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
    setImageFile(null);
    setImagePreview(null);
  };

  // Toggle experience selection
  const toggleExperience = (experience) => {
    setCompanionData(prev => {
      const isSelected = prev.experiences.some(exp => exp.id === experience.id);

      if (isSelected) {
        // Remove experience
        return {
          ...prev,
          experiences: prev.experiences.filter(exp => exp.id !== experience.id)
        };
      } else {
        // Add experience (max 2)
        if (prev.experiences.length >= 2) {
          toast.error('You can only select 2 experiences');
          return prev;
        }
        return {
          ...prev,
          experiences: [...prev.experiences, experience]
        };
      }
    });
  };

  // Add custom experience
  const handleAddCustomExperience = () => {
    if (!customExperience.trim()) {
      toast.error('Please enter an experience description');
      return;
    }

    if (companionData.experiences.length >= 2) {
      toast.error('You can only select 2 experiences');
      return;
    }

    const customExp = {
      id: `custom-${Date.now()}`,
      name: customExperience.trim(),
      bonus: 2,
      custom: true
    };

    setCompanionData(prev => ({
      ...prev,
      experiences: [...prev.experiences, customExp]
    }));

    setCustomExperience('');
    toast.success('Custom experience added!');
  };

  // Create companion
  const handleCreate = async () => {
    if (!rangerId || !user) {
      toast.error('Ranger character not found');
      return;
    }

    setCreating(true);
    setUploadingImage(false); // Reset at start

    try {
      let imageUrl = '';

      // Upload image if file is selected
      if (imageFile) {
        setUploadingImage(true);
        const uploadToast = toast.loading('Uploading image...');
        try {
          imageUrl = await uploadCompanionImage(imageFile, user.uid, `temp_${Date.now()}`);
          toast.dismiss(uploadToast);
          toast.success('Image uploaded!');
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          toast.dismiss(uploadToast);

          // Check if it's a CORS/Storage error
          if (uploadError.message?.includes('CORS') ||
              uploadError.message?.includes('Storage') ||
              uploadError.code === 'storage/unauthenticated' ||
              uploadError.code === 'storage/unauthorized') {
            toast.error('Image upload failed (CORS/Storage config). Continuing without image upload.');
          } else {
            toast.error('Image upload failed. Continuing without uploaded image.');
          }
        } finally {
          // Always reset uploading state
          setUploadingImage(false);
        }
      }

      // Create companion with image URL and upgrade selections
      const companionDataWithImage = {
        ...companionData,
        imageUrl: imageUrl || companionData.imageUrl,
        upgradeSelections: upgradeSelections // Include selected upgrades
      };

      const companionId = await createCompanion(rangerId, companionDataWithImage);

      if (onComplete) {
        onComplete(companionId);
      } else {
        // Navigate to companion sheet
        navigate(`/character/${rangerId}/companion`);
      }
    } catch (error) {
      console.error('Error creating companion:', error);
      // Error toast handled by service
    } finally {
      setCreating(false);
      setUploadingImage(false);
    }
  };

  // Cancel and go back
  const handleCancel = () => {
    if (onComplete) {
      onComplete(null);
    } else {
      navigate(`/character/${rangerId}`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6 flex items-center justify-center min-h-screen">
        <p className="text-xl text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Create Beast Companion</h1>
        <p className="text-gray-600 dark:text-gray-400">
          Step {step} of {totalSteps}
          {ranger && ranger.level > 1 && (
            <span className="ml-2 text-sm text-indigo-600 dark:text-indigo-400">
              (Ranger Level {ranger.level} - {upgradesNeeded} upgrade{upgradesNeeded !== 1 ? 's' : ''} needed)
            </span>
          )}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          {Array.from({ length: totalSteps }, (_, i) => i + 1).map((s) => (
            <div key={s} className="flex items-center flex-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                  s < step
                    ? 'bg-green-500 text-white'
                    : s === step
                    ? 'bg-indigo-500 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}
              >
                {s < step ? <IconCheck /> : s}
              </div>
              {s < totalSteps && (
                <div
                  className={`flex-1 h-1 mx-2 ${
                    s < step ? 'bg-green-500' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 mb-6 shadow-md">
        {/* Step 1: Name & Type */}
        {step === 1 && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Name Your Companion</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Choose a name and describe what kind of animal your companion is.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Companion Name *
                </label>
                <input
                  type="text"
                  value={companionData.name}
                  onChange={(e) =>
                    setCompanionData({ ...companionData, name: e.target.value })
                  }
                  placeholder="e.g., Shadow, Fang, Whiskers"
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 focus:outline-none bg-white dark:bg-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Animal Type *
                </label>
                <input
                  type="text"
                  value={companionData.animalType}
                  onChange={(e) =>
                    setCompanionData({ ...companionData, animalType: e.target.value })
                  }
                  placeholder="e.g., Wolf, Hawk, Bear, Panther"
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 focus:outline-none bg-white dark:bg-gray-700"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Companion Image (Optional)
                </label>

                {/* Image Preview */}
                {imagePreview && (
                  <div className="mb-4 relative">
                    <img
                      src={imagePreview}
                      alt="Companion preview"
                      loading="lazy"
                      className="w-full max-w-xs h-48 object-cover rounded-lg border-2 border-indigo-500"
                    />
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                    >
                      ✕
                    </button>
                  </div>
                )}

                {/* File Upload */}
                {!imagePreview && (
                  <div className="mb-3 space-y-2">
                    <input
                      id="companion-image-upload"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageSelect}
                      style={{ display: 'none' }}
                    />

                    {/* Method 1: Label-based (standard) */}
                    <label
                      htmlFor="companion-image-upload"
                      className="block w-full p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-center cursor-pointer hover:border-indigo-500 transition-colors"
                    >
                      <div className="text-gray-600 dark:text-gray-400">
                        <p className="font-medium">Click to upload an image</p>
                        <p className="text-sm mt-1">JPG, PNG, GIF, or WebP (max 5MB)</p>
                        <p className="text-xs text-indigo-500 mt-2">
                          💡 Future: AI-generated images coming soon!
                        </p>
                      </div>
                    </label>

                    {/* Method 2: Button fallback (if label doesn't work) */}
                    <button
                      type="button"
                      onClick={triggerFileInput}
                      className="w-full px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors text-sm"
                    >
                      Alternative: Click here to browse files
                    </button>
                  </div>
                )}

                {/* Alternative: Image URL */}
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 mb-2">
                  or
                </div>
                <input
                  type="url"
                  value={companionData.imageUrl}
                  onChange={(e) =>
                    setCompanionData({ ...companionData, imageUrl: e.target.value })
                  }
                  placeholder="https://example.com/companion-image.jpg"
                  disabled={!!imagePreview}
                  className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 focus:outline-none bg-white dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Paste an image URL as an alternative to uploading
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Choose Experiences */}
        {step === 2 && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Choose 2 Experiences (+2 Modifier)</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Select 2 experiences that define your companion's strengths. Each grants +2 to relevant rolls.
            </p>

            <div className="mb-4 text-sm font-medium">
              Selected: {companionData.experiences.length} / 2
            </div>

            {/* Selected Experiences Display */}
            {companionData.experiences.length > 0 && (
              <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700">
                <h3 className="font-semibold text-sm mb-2">Selected Experiences:</h3>
                <div className="space-y-2">
                  {companionData.experiences.map((exp) => (
                    <div
                      key={exp.id}
                      className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded"
                    >
                      <span className="font-medium">{exp.name}</span>
                      <button
                        onClick={() => toggleExperience(exp)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Preset Experiences */}
            <h3 className="font-semibold mb-3">Preset Experiences</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {COMPANION_EXPERIENCES.map((experience) => {
                const isSelected = companionData.experiences.some(
                  (exp) => exp.id === experience.id
                );

                return (
                  <div
                    key={experience.id}
                    onClick={() => toggleExperience(experience)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/50'
                        : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-bold text-lg">{experience.name}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          +{experience.bonus} to relevant rolls
                        </p>
                      </div>
                      {isSelected && (
                        <div className="text-indigo-500">
                          <IconCheck />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Custom Experience */}
            <div className="border-t border-gray-300 dark:border-gray-600 pt-6">
              <h3 className="font-semibold mb-2">Or Create Custom Experience</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                Write a short phrase about your companion's unique skill or trait. (e.g., "Expert swimmer", "Keen nose for danger")
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={customExperience}
                  onChange={(e) => setCustomExperience(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddCustomExperience();
                    }
                  }}
                  placeholder="Describe your companion's experience..."
                  className="flex-1 p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 focus:outline-none bg-white dark:bg-gray-700"
                  disabled={companionData.experiences.length >= 2}
                />
                <button
                  onClick={handleAddCustomExperience}
                  disabled={!customExperience.trim() || companionData.experiences.length >= 2}
                  className="px-4 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Describe Attack */}
        {step === 3 && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Describe Attack</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Describe how your companion attacks. Your companion deals d6 damage at Melee range,
              using your Proficiency modifier.
            </p>

            <div className="mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg border border-indigo-200 dark:border-indigo-700">
              <h3 className="font-bold mb-2">Attack Stats</h3>
              <ul className="text-sm space-y-1">
                <li>• <strong>Damage:</strong> d6 (starts, can be upgraded)</li>
                <li>• <strong>Range:</strong> Melee (can be upgraded to Close or Far)</li>
                <li>• <strong>Modifier:</strong> Your Proficiency modifier</li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Attack Description *
              </label>
              <textarea
                value={companionData.attackDescription}
                onChange={(e) =>
                  setCompanionData({
                    ...companionData,
                    attackDescription: e.target.value
                  })
                }
                placeholder="e.g., 'Pounces and bites with sharp fangs' or 'Swoops down and rakes with talons'"
                rows={4}
                className="w-full p-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 focus:outline-none bg-white dark:bg-gray-700"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                This is for flavor - describe how your companion attacks in combat.
              </p>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === 4 && (
          <div>
            <h2 className="text-2xl font-bold mb-4">Review Your Companion</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Review your companion's details before {upgradesNeeded > 0 ? 'selecting upgrades' : 'creating'}.
            </p>

            <div className="space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="font-bold text-lg mb-2">Basic Information</h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                  <p><strong>Name:</strong> {companionData.name}</p>
                  <p><strong>Animal Type:</strong> {companionData.animalType}</p>
                  {companionData.imageUrl && (
                    <p><strong>Image:</strong> {companionData.imageUrl}</p>
                  )}
                </div>
              </div>

              {/* Experiences */}
              <div>
                <h3 className="font-bold text-lg mb-2">Experiences</h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                  {companionData.experiences.map((exp) => (
                    <div key={exp.id}>
                      <strong>{exp.name}</strong> (+{exp.bonus})
                    </div>
                  ))}
                </div>
              </div>

              {/* Attack */}
              <div>
                <h3 className="font-bold text-lg mb-2">Attack</h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                  <p><strong>Description:</strong> {companionData.attackDescription}</p>
                  <p><strong>Damage:</strong> d6</p>
                  <p><strong>Range:</strong> Melee</p>
                </div>
              </div>

              {/* Starting Stats */}
              <div>
                <h3 className="font-bold text-lg mb-2">Starting Stats</h3>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg space-y-2">
                  <p><strong>Evasion:</strong> 10</p>
                  <p><strong>Max Stress:</strong> 3</p>
                </div>
              </div>

              {/* Level-Up Notice */}
              {upgradesNeeded > 0 && (
                <div className="p-4 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg border border-yellow-300 dark:border-yellow-700">
                  <p className="font-bold text-yellow-800 dark:text-yellow-200">
                    🎓 Level-Up Required
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    Your ranger is level {ranger.level}. Your companion needs {upgradesNeeded} upgrade{upgradesNeeded > 1 ? 's' : ''} to catch up.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upgrade Selection Steps (5+) */}
        {step > 4 && step <= 4 + upgradesNeeded && (() => {
          const upgradeStepIndex = step - 5;
          const currentUpgradeLevel = upgradeStepIndex + 2; // Level 2 for first upgrade, etc.
          const selectedUpgrade = upgradeSelections[upgradeStepIndex];

          // Calculate current stats based on previous upgrades
          let currentDamageDie = 'd6';
          let currentDamageRange = 'Melee';
          for (let i = 0; i < upgradeStepIndex; i++) {
            const prevUpgrade = upgradeSelections[i];
            if (prevUpgrade?.id === 'vicious') {
              // Default to upgrading damage die first
              const dieProgression = ['d6', 'd8', 'd10', 'd12'];
              const currentIndex = dieProgression.indexOf(currentDamageDie);
              if (currentIndex < dieProgression.length - 1) {
                currentDamageDie = dieProgression[currentIndex + 1];
              }
            }
          }

          return (
            <div>
              <h2 className="text-2xl font-bold mb-4">
                Level {currentUpgradeLevel} Upgrade ({upgradeStepIndex + 1} of {upgradesNeeded})
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Choose an upgrade for your companion as they reach level {currentUpgradeLevel}.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {COMPANION_UPGRADES.map((upgrade) => {
                  const isSelected = selectedUpgrade?.id === upgrade.id;

                  return (
                    <div
                      key={upgrade.id}
                      onClick={() => {
                        const newSelections = [...upgradeSelections];
                        newSelections[upgradeStepIndex] = upgrade;
                        setUpgradeSelections(newSelections);
                      }}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/50'
                          : 'border-gray-200 dark:border-gray-700 hover:border-indigo-400'
                      }`}
                    >
                      <h3 className="font-bold text-lg mb-2">{upgrade.name}</h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {upgrade.description}
                      </p>
                      {upgrade.affectsRanger && (
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-2 font-medium">
                          ⭐ Affects Ranger
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedUpgrade && (
                <div>
                  {/* Special configuration for Intelligent */}
                  {selectedUpgrade.id === 'intelligent' && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                      <p className="text-sm font-medium mb-2">
                        Note: This upgrade will improve your first experience ({companionData.experiences[0]?.name || 'Experience 1'}) by +1.
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        You can adjust experience bonuses later if needed.
                      </p>
                    </div>
                  )}

                  {/* Special configuration for Vicious */}
                  {selectedUpgrade.id === 'vicious' && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/30 rounded-lg border border-blue-200 dark:border-blue-700">
                      <p className="text-sm font-medium mb-2">
                        Note: This upgrade will improve your companion's damage die.
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Current: {currentDamageDie} → Next: {
                          currentDamageDie === 'd6' ? 'd8' :
                          currentDamageDie === 'd8' ? 'd10' :
                          currentDamageDie === 'd10' ? 'd12' : '(max)'
                        }
                      </p>
                    </div>
                  )}

                  <div className="p-4 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-700">
                    <p className="text-sm text-green-800 dark:text-green-200">
                      ✓ <strong>{selectedUpgrade.name}</strong> selected for level {currentUpgradeLevel}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center">
        <div>
          {step > 1 && (
            <button
              onClick={prevStep}
              disabled={creating}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors disabled:opacity-50"
            >
              <IconChevronLeft />
              Previous
            </button>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            disabled={creating}
            className="px-6 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>

          {step < totalSteps ? (
            <button
              onClick={nextStep}
              disabled={!canProceed()}
              className="px-6 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          ) : (
            <button
              onClick={handleCreate}
              disabled={creating || uploadingImage}
              className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploadingImage
                ? 'Uploading Image...'
                : creating
                ? 'Creating Companion...'
                : 'Create Companion'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanionCreator;
